import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Heart, MapPin, Clock, Star, Users, ExternalLink, 
  Phone, Car, Accessibility, Baby, ChevronRight, Calendar, Sun, Cloud, Loader2, Send, Image, X,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';
import CommentSection from '../components/CommentSection';
import ShareButton from '../components/ShareButton';

const API = process.env.REACT_APP_BACKEND_URL;

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [saveAnimating, setSaveAnimating] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewImages, setReviewImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [beenThere, setBeenThere] = useState(false);
  const [beenThereCount, setBeenThereCount] = useState(0);

  const priceLabels = {
    free: t('price_free'),
    budget: language === 'en' ? 'Budget (Under €10)' : language === 'nl' ? 'Budget (Onder €10)' : 'Budget (Moins de 10€)',
    'mid-range': language === 'en' ? 'Mid-Range (€10-30)' : language === 'nl' ? 'Gemiddeld (€10-30)' : 'Moyen (10-30€)',
    premium: language === 'en' ? 'Premium (€30+)' : language === 'nl' ? 'Premium (€30+)' : 'Premium (30€+)'
  };

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const response = await axios.get(`${API}/api/activities/${id}`, {
          withCredentials: true
        });
        setActivity(response.data);
        setIsSaved(response.data.is_saved || false);
        setBeenThere(response.data.been_there || false);
        setBeenThereCount(response.data.been_there_count || 0);
      } catch (error) {
        console.error('Error fetching activity:', error);
        navigate('/discover');
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [id, navigate]);

  const handleSave = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setSaveAnimating(true);
    try {
      const response = await axios.post(
        `${API}/api/activities/${id}/save`,
        {},
        { withCredentials: true }
      );
      setIsSaved(response.data.saved);
    } catch (error) {
      console.error('Save error:', error);
    }
    setTimeout(() => setSaveAnimating(false), 400);
  };

  const handleBeenThere = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const response = await axios.post(`${API}/api/activities/${id}/been-there`, {}, { withCredentials: true });
      setBeenThere(response.data.been_there);
      setBeenThereCount(prev => response.data.been_there ? prev + 1 : Math.max(0, prev - 1));
    } catch (error) { console.error('Been there error:', error); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReviewImages(prev => [...prev, `${API}${response.data.url}`]);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (index) => {
    setReviewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API}/api/reviews`, {
        activity_id: id,
        rating: reviewRating,
        comment: reviewComment,
        images: reviewImages
      }, { withCredentials: true });
      
      // Refresh activity data
      const response = await axios.get(`${API}/api/activities/${id}`, {
        withCredentials: true
      });
      setActivity(response.data);
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      setReviewImages([]);
    } catch (error) {
      console.error('Review error:', error);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
      </div>
    );
  }

  if (!activity) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[300px]">
        <img 
          src={activity.images?.[0] || 'https://via.placeholder.com/800x600'} 
          alt={activity.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 gradient-overlay" />
        
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center z-10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
        </button>

        {/* Action buttons */}
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button 
            onClick={handleBeenThere}
            className={`h-10 px-3 rounded-full bg-white/90 flex items-center justify-center gap-1.5 ${beenThere ? '' : ''}`}
            data-testid="been-there-btn"
          >
            <CheckCircle2 className={`w-5 h-5 ${beenThere ? 'fill-[#81B29A] text-[#81B29A]' : 'text-[#2C302E]'}`} />
            {beenThereCount > 0 && <span className="text-xs font-medium text-[#2C302E]">{beenThereCount}</span>}
          </button>
          <button 
            onClick={handleSave}
            className={`w-10 h-10 rounded-full bg-white/90 flex items-center justify-center ${saveAnimating ? 'heart-beat' : ''}`}
            data-testid="save-activity-btn"
          >
            <Heart className={`w-5 h-5 ${isSaved ? 'fill-[#E07A5F] text-[#E07A5F]' : 'text-[#2C302E]'}`} />
          </button>
          <ShareButton activity={activity} />
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="flex flex-wrap gap-2 mb-3">
            {activity.trending_score > 80 && (
              <span className="badge bg-white/90 text-[#E07A5F]">Trending</span>
            )}
            {activity.grandparent_friendly && (
              <span className="badge bg-white/90 text-[#81B29A]">Grandparent Friendly</span>
            )}
            <span className={`badge ${
              activity.price_range === 'free' ? 'badge-free' : 
              activity.price_range === 'premium' ? 'badge-premium' : 'badge-secondary'
            } !bg-opacity-90`}>
              {priceLabels[activity.price_range]?.split(' ')[0]}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold drop-shadow-lg font-['Outfit']">{activity.title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 -mt-4 relative z-10">
        {/* Quick Info Card */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <div className="grid grid-cols-3 gap-4 text-center mb-6">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-2">
                <MapPin className="w-6 h-6 text-[#E07A5F]" />
              </div>
              <p className="text-sm text-[#666B67]">{activity.city}</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-2xl bg-[#81B29A]/10 flex items-center justify-center mx-auto mb-2">
                <Clock className="w-6 h-6 text-[#81B29A]" />
              </div>
              <p className="text-sm text-[#666B67]">{activity.duration_hours}h visit</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
                <Star className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-sm text-[#666B67]">{activity.avg_rating || '-'} ({activity.review_count})</p>
            </div>
          </div>

          {/* Age suitability */}
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-[#666B67]" />
            <span className="text-sm text-[#666B67]">Ages: </span>
            <div className="flex gap-1">
              {activity.age_suitability?.map(age => (
                <span key={age} className="badge badge-secondary">{age}</span>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-3">
            {activity.buggy_friendly && (
              <div className="flex items-center gap-1 text-sm text-[#666B67]">
                <Baby className="w-4 h-4" />
                <span>Buggy friendly</span>
              </div>
            )}
            {activity.parking_info && (
              <div className="flex items-center gap-1 text-sm text-[#666B67]">
                <Car className="w-4 h-4" />
                <span>Parking available</span>
              </div>
            )}
            {activity.accessibility_info && (
              <div className="flex items-center gap-1 text-sm text-[#666B67]">
                <Accessibility className="w-4 h-4" />
                <span>Accessible</span>
              </div>
            )}
            {activity.weather_suitability && (
              <div className="flex items-center gap-1 text-sm text-[#666B67]">
                {activity.weather_suitability === 'sun' ? <Sun className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
                <span>{activity.weather_suitability === 'any' ? 'Any weather' : activity.weather_suitability}</span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <h2 className="text-lg font-semibold text-[#2C302E] mb-3 font-['Outfit']">About</h2>
          <p className="text-[#666B67] mb-4">{activity.summary}</p>
          
          {activity.why_families_love_it && (
            <>
              <h3 className="text-md font-semibold text-[#2C302E] mb-2 font-['Outfit']">Why families love it</h3>
              <p className="text-[#666B67]">{activity.why_families_love_it}</p>
            </>
          )}
        </div>

        {/* Location & Contact */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']">Location & Contact</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#E07A5F] mt-0.5" />
              <div>
                <p className="text-[#2C302E]">{activity.address}</p>
                <p className="text-sm text-[#666B67]">{activity.city}, {activity.postcode}</p>
              </div>
            </div>

            {activity.opening_times && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#81B29A] mt-0.5" />
                <div>
                  <p className="text-[#2C302E]">Opening Times</p>
                  <p className="text-sm text-[#666B67]">{activity.opening_times}</p>
                </div>
              </div>
            )}

            {activity.phone && (
              <a 
                href={`tel:${activity.phone}`}
                className="flex items-center gap-3 text-[#E07A5F] hover:underline"
              >
                <Phone className="w-5 h-5" />
                <span>{activity.phone}</span>
              </a>
            )}

            {activity.website && (
              <a 
                href={activity.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[#E07A5F] hover:underline"
              >
                <ExternalLink className="w-5 h-5" />
                <span>Visit website</span>
              </a>
            )}
          </div>

          {/* Map placeholder */}
          <div className="mt-4 h-40 rounded-2xl bg-[#F9F8F6] flex items-center justify-center">
            <button
              onClick={() => navigate(`/map?lat=${activity.latitude}&lng=${activity.longitude}`)}
              className="btn-secondary"
              data-testid="view-map-btn"
            >
              View on Map
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#2C302E] font-['Outfit']">Reviews</h2>
            {user && !showReviewForm && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReviewForm(true)}
                className="text-[#E07A5F] border-[#E07A5F]/20"
                data-testid="write-review-btn"
              >
                Write Review
              </Button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-[#F9F8F6] rounded-2xl">
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#2C302E] mb-2">Rating</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewRating(star)}
                      className="p-1"
                    >
                      <Star className={`w-6 h-6 ${star <= reviewRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <Textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Share your experience..."
                  className="min-h-[100px]"
                  required
                  data-testid="review-comment-input"
                />
              </div>
              
              {/* Photo Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('common_add_photos')}</label>
                <div className="flex flex-wrap gap-2">
                  {reviewImages.map((img, index) => (
                    <div key={index} className="relative w-16 h-16">
                      <img src={img} alt="" className="w-full h-full object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-16 h-16 border-2 border-dashed border-[#2C302E]/20 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#E07A5F] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    {uploadingImage ? (
                      <Loader2 className="w-5 h-5 animate-spin text-[#666B67]" />
                    ) : (
                      <Image className="w-5 h-5 text-[#666B67]" />
                    )}
                  </label>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowReviewForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingReview}
                  className="flex-1 btn-primary"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Reviews List */}
          {activity.reviews?.length > 0 ? (
            <div className="space-y-4">
              {activity.reviews.map((review) => (
                <div key={review.id} className="border-b border-[#2C302E]/5 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-[#81B29A]/20 flex items-center justify-center">
                      <span className="text-[#81B29A] font-medium">
                        {review.user_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-[#2C302E]">{review.user_name}</p>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-3 h-3 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[#666B67]">{review.comment}</p>
                  {review.images && review.images.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {review.images.map((img, imgIndex) => (
                        <img 
                          key={imgIndex} 
                          src={img} 
                          alt="" 
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#666B67] text-center py-8">{t('detail_no_reviews')}</p>
          )}
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-2xl p-5 mt-6" data-testid="comments-container">
          <CommentSection activityId={id} />
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ActivityDetail;
